import React, { createContext, useContext, useMemo } from 'react';
import { getFlowConfig, detectFlowType, FLOW_TYPES } from '../config/flowConfigs';

// Create the Flow Context
const FlowContext = createContext();

// Flow Provider Component
export const FlowProvider = ({ children, flowType = null }) => {
  // Determine flow type (allow override via prop, otherwise detect)
  const currentFlowType = flowType || detectFlowType();
  // Get the flow configuration
  const flowConfig = useMemo(() => getFlowConfig(currentFlowType), [currentFlowType]);
  
  // Provide helper methods
  const flowUtils = useMemo(() => ({
    // Check if current flow is a specific type
    isInsuranceProfile: () => currentFlowType === FLOW_TYPES.INSURANCE_PROFILE,
    isExtendedWarranty: () => currentFlowType === FLOW_TYPES.EXTENDED_WARRANTY,
    isInsuranceRenewal: () => currentFlowType === FLOW_TYPES.INSURANCE_RENEWAL,
    
    
    // Check if a step should show progress indicator
    shouldShowProgress: (step) => {
      if (!flowConfig.ui.showProgressIndicator) return false;
      return !flowConfig.ui.progressExcludeSteps.includes(step);
    },
    
    // Get survey ID for a specific survey type
    getSurveyId: (surveyType) => {
      return flowConfig.surveys[surveyType];
    },
    
    // Check if a feature is enabled
    isFeatureEnabled: (featureName) => {
      return flowConfig.features[featureName] || false;
    },
    
    // Get config value
    getConfig: (path) => {
      return path.split('.').reduce((obj, key) => obj?.[key], flowConfig);
    }
  }), [currentFlowType, flowConfig]);

  const value = {
    flowType: currentFlowType,
    config: flowConfig,
    ...flowUtils
  };

  return (
    <FlowContext.Provider value={value}>
      {children}
    </FlowContext.Provider>
  );
};

// Custom hook to use the Flow Context
export const useFlow = () => {
  const context = useContext(FlowContext);
  
  if (!context) {
    throw new Error('useFlow must be used within a FlowProvider');
  }
  
  return context;
};

// Helper hook for flow-specific logic
export const useFlowSteps = () => {
  const { config } = useFlow();
  return config.steps;
};

// Helper hook for flow-specific routing
export const useFlowRouting = () => {
  const { config } = useFlow();
  
  return {
    baseRoute: config.baseRoute,
    defaultRoute: config.defaultRoute,
  };
};
